import { CalendarIcon, ClockIcon, DocumentTextIcon, ExternalLinkIcon, HashtagIcon, LightningBoltIcon, PhotographIcon, SparklesIcon, StarIcon, TagIcon, ViewGridIcon } from "@heroicons/react/outline";
import { PencilIcon } from "@heroicons/react/solid";
import { useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { DescriptionItem } from "components/descriptionItem";
import { FullScreenLoading } from "components/fullScreenLoading";
import { HistoryLodModal } from "components/historyLogModal";
import { IBlogDetail, SupportedLanguage } from "interfaces";
import { useState } from "react";
import { Link } from "react-router-dom";
import CONFIG from "app-config/config.json";
import moment from "moment";
import { CONTENT_LANGUAGES } from "utills/constants";
import { get } from "lodash";
import { isHaveActionPermission } from "utills/helpers";

export const BlogDetail: React.FC = () => {
  const [openLogModal, setOpenLogModal] = useState(false);
  const [selectedContentLanguage, setSelectedContentLanguage] = useState(SupportedLanguage.EN);

  const parse = useParse();
  const { params } = parse();
  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<IBlogDetail>({
    resource: "mkt/blogs",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const blogDetail = data?.data ?? null;

  if (isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Blog Detail"
        additionalLabel={
          <div className="flex gap-x-2.5">
            <Button
              className="bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200"
              onClick={() => setOpenLogModal(true)}
            >
              <span className="sr-only">History Log</span>
              <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
              <span className="hidden sm:block sm:ml-2">Log</span>
            </Button>
            {isHaveActionPermission("blog", "mkt", "edit") ? (
              <Link to={`/mkt/blog/${blogDetail?.id}/edit`}>
                <Button>
                  <span className="sr-only">Edit</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Edit</span>
                </Button>
              </Link>
            ) : (
              <></>
            )}
          </div>
        }
      />
      <div className="box-container p-0">
        <div className="divide-y divide-gray-100">
          <DescriptionItem
            icon={TagIcon}
            label="Status"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {blogDetail?.is_active ? "Active" : "Inactive"}
              </p>
            }
          />
          <DescriptionItem
            icon={LightningBoltIcon}
            label="Hot position"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {blogDetail?.is_hot_position ? "Yes" : "No"}
              </p>
            }
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Banner"
            content={
              blogDetail?.banner_url ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={blogDetail?.banner_url}
                  alt="Blog Banner"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={StarIcon}
            label="Title vie"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {blogDetail?.title_vi}
              </p>
            }
          />
          <DescriptionItem
            icon={StarIcon}
            label="Title eng"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {blogDetail?.title_en}
              </p>
            }
          />
          <DescriptionItem
            icon={ExternalLinkIcon}
            label="Slug"
            content={
              <Link 
                to={`${CONFIG.DOMAIN}/news/detail/${blogDetail?.slug}`} 
                target="_blank"
                className="hover:underline text-brand-500"
              >
                <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                  {CONFIG.DOMAIN}/news/detail/{blogDetail?.slug}
                </p>
              </Link>
            }
          />
          <DescriptionItem
            icon={ViewGridIcon}
            label="Category eng"
            content={
              <p className="flex gap-x-2 text-sm capitalize leading-6 sm:col-span-4">
                {blogDetail?.category}
              </p>
            }
          />
          <DescriptionItem
            icon={ViewGridIcon}
            label="Sub-category eng"
            content={
              <p className="flex gap-x-2 text-sm capitalize leading-6 sm:col-span-4">
                {blogDetail?.sub_category_name_en}
              </p>
            }
          />
          <DescriptionItem
            icon={CalendarIcon}
            label="Publish at"
            content={
              <p className="flex gap-x-2 text-sm capitalize leading-6 sm:col-span-4">
                {blogDetail?.publish_at ? moment(blogDetail.publish_at).format("DD/MM/YYYY HH:mm:ss") : ""}
              </p>
            }
          />
          <DescriptionItem
            icon={HashtagIcon}
            label="Hashtag"
            content={
              <p className="div text-sm leading-6 sm:col-span-4">
                {(blogDetail?.tags ?? []).map((tag, index) => (
                  <p className="inline" key={index}>
                    {index !== 0 ? <span className="mx-2">•</span> : ""}
                    {tag}
                  </p>
                ))}
              </p>
            }
          />
          <DescriptionItem
            icon={SparklesIcon}
            label="Related blogs"
            content={(
              <div>
                {(blogDetail?.related_blogs ?? []).map((blog, index) => (
                  <p className="text-sm leading-6" key={blog.id}>
                    {blog.title_en}
                  </p>
                ))}
              </div>
            )}
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Short Description vie"
            content={(
              <p className="text-sm leading-6 sm:col-span-4">
                {blogDetail?.short_description_vi}
              </p>
            )}
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Short Description eng"
            content={(
              <p className="text-sm leading-6 sm:col-span-4">
                {blogDetail?.short_description_en}
              </p>
            )}
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Content"
            additionalLabel={(
              <div className="flex flex-row gap-x-1.5 p-1.5 w-fit rounded-full outline outline-solid outline-[1px] outline-brand-500">
                {CONTENT_LANGUAGES.map((lang, index) => (
                  <img
                    key={lang.value}
                    src={`/images/flags/circle-${lang.value}.png`}
                    alt="Language Icon"
                    className={`w-4 shrink-0 cursor-pointer ${lang.value !== selectedContentLanguage ? 'opacity-20' : ''}`}
                    onClick={() => setSelectedContentLanguage(lang.value)}
                  />
                ))}
              </div>
            )}
            content={
              <div>
                <div
                  className="mt-4 reset-tw"
                  dangerouslySetInnerHTML={{
                    __html: `${get(
                      blogDetail,
                        `content_${selectedContentLanguage}`,
                      ""
                    )}`,
                  }}
                ></div>
              </div>
            }
          />
        </div>
      </div>
      <HistoryLodModal
        resource={`mkt/blogs/${params?.id}/history`}
        open={openLogModal}
        onClose={() => setOpenLogModal(false)}
        key="Blog"
        id="blog"
      />
      {(isFetching && isPreviousData) ? <FullScreenLoading /> : <></>}
    </div>
  );
}